import { Node, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    admonition: {
      setAdmonition: (attrs: { type: AdmonitionType; title?: string }) => ReturnType;
    };
  }
}

export type AdmonitionType =
  | 'note'
  | 'abstract'
  | 'info'
  | 'tip'
  | 'success'
  | 'question'
  | 'warning'
  | 'failure'
  | 'danger'
  | 'bug'
  | 'example'
  | 'quote';

const ADMONITION_COLORS: Record<string, string> = {
  note: '#448aff',
  abstract: '#00b0ff',
  info: '#00b8d4',
  tip: '#00bfa5',
  success: '#00c853',
  question: '#64dd17',
  warning: '#ff6d00',
  failure: '#ff1744',
  danger: '#ff1744',
  bug: '#f50057',
  example: '#7c4dff',
  quote: '#9e9e9e',
};

const ADMONITION_ICONS: Record<string, string> = {
  note: '📝',
  abstract: '📋',
  info: 'ℹ️',
  tip: '💡',
  success: '✅',
  question: '❓',
  warning: '⚠️',
  failure: '❌',
  danger: '🔥',
  bug: '🐛',
  example: '📖',
  quote: '💬',
};

export const AdmonitionNode = Node.create({
  name: 'admonition',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      type: {
        default: 'note',
        parseHTML: (el) => el.getAttribute('data-type') || 'note',
        renderHTML: (attrs) => ({ 'data-type': attrs['type'] }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') || '',
        renderHTML: (attrs) => ({ 'data-title': attrs['title'] }),
      },
      collapsible: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-collapsible') === 'true',
        renderHTML: (attrs) => ({
          'data-collapsible': String(attrs['collapsible']),
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type][class*="admonition"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const type = node.attrs['type'] as string;
    const title = node.attrs['title'] as string || type.charAt(0).toUpperCase() + type.slice(1);
    const color = ADMONITION_COLORS[type] || '#448aff';
    const icon = ADMONITION_ICONS[type] || '📝';

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: `admonition admonition-${type}`,
        style: `border-left: 4px solid ${color}; padding: 0.75em 1em; margin: 1em 0; background: ${color}18; border-radius: 4px;`,
      }),
      [
        'div',
        {
          class: 'admonition-title',
          contenteditable: 'false',
          style: `color: ${color}; font-weight: 700; font-size: 0.9em; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5em; user-select: none;`,
        },
        `${icon} ${title}`,
      ],
      ['div', { class: 'admonition-body' }, 0],
    ];
  },

  addCommands() {
    return {
      setAdmonition:
        (attrs: { type: AdmonitionType; title?: string }) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ commands }: { commands: any }) => {
          return commands.wrapIn(this.name, attrs);
        },
    };
  },
});
